import ENDPOINTS from 'tradera-constants/endpoints';
import { axiosConfigs } from 'tradera-utils/api';
import campaignCodeActions from './campaign-code-actions';
import { defaultClient } from 'tradera-utils/http';
import { selectCategoryAttributes } from '../selectors/attribute-selectors';
import ThrottledAction from 'tradera-apps/syi/script/app_react/utils/throttled-action';
const throttleTimeout = 2000;
const suggestionsThrottle = new ThrottledAction(throttleTimeout, true);

const INPUT_TYPE_MAP = {
    Button: 'button',
    Color: 'color',
    Dropdown: 'dropdown'
};

const transformServerOption = serverOption => {
    return {
        value:
            typeof serverOption === 'string'
                ? serverOption
                : serverOption.value,
        isActive: false,
        warningLabel: {
            title:
                serverOption.warningLabel !== undefined &&
                serverOption.warningLabel !== null
                    ? serverOption.warningLabel.title
                    : null,
            text:
                serverOption.warningLabel !== undefined &&
                serverOption.warningLabel !== null
                    ? serverOption.warningLabel.text
                    : null
        }
    };
};

const transformServerAttribute = attribute => {
    return {
        id: attribute.id,
        title: attribute.displayName,
        description: attribute.description,
        hiddenCategories: attribute.hiddenCategories,
        sortOrder: attribute.sortOrder,
        backingField: attribute.backingField,
        options: attribute.possibleValues.map(transformServerOption),
        inputType: INPUT_TYPE_MAP[attribute.type],
        isMultiSelect: attribute.isMultiSelect,
        maxNumberOfValues: attribute.maxNumberOfValues,
        minNumberOfValues: attribute.minNumberOfValues,
        numberOfValuesSelected: 0,
        shouldTranslateValues: attribute.shouldTranslateValues
    };
};

export const setAttributesFromDraft = draft => dispatch => {
    dispatch({
        type: 'SET_DRAFT_ATTRIBUTES',
        payload: draft.attributeValues
    });
};

export const fetchForCategory = categoryId => async dispatch => {
    dispatch({
        type: 'RESET_ATTRIBUTES'
    });

    if (!categoryId) {
        return;
    }

    try {
        const response = await defaultClient.get(
            `${ENDPOINTS.SELLING_ATTRIBUTES}/?categoryId=${categoryId}`,
            {
                ...axiosConfigs.authenticated
            }
        );
        dispatch({
            type: 'SET_CATEGORY_ATTRIBUTES',
            payload: response.data.attributeDefinitions.map(
                transformServerAttribute
            )
        });
        dispatch(updateCategoryAttributesOptionsStatuses());
        dispatch(campaignCodeActions.validateCampaignCode());
    } catch (error) {
        dispatch({
            type: 'RESET_ATTRIBUTES'
        });
        throw error;
    }
};

const updateCategoryAttributesOptionsStatuses = () => dispatch => {
    dispatch({
        type: 'UPDATE_CATEGORY_ATTRIBUTES_OPTIONS_STATUSES'
    });
};

export const updateAttribute = (
    attributeId,
    optionValue,
    activate,
    onUpdated
) => (dispatch, getState) => {
    const {
        maxNumberOfValues,
        numberOfValuesSelected
    } = selectCategoryAttributes(getState()).find(a => a.id === attributeId);

    if (activate && maxNumberOfValues === numberOfValuesSelected) {
        return;
    }

    dispatch({
        type: 'SET_CATEGORY_ATTRIBUTE_OPTION',
        payload: {
            attributeId,
            optionValue,
            activate
        }
    });
    dispatch(campaignCodeActions.validateCampaignCode());
    if (onUpdated) {
        onUpdated();
    }
};

export const resetAttribute = attributeId => dispatch => {
    dispatch({
        type: 'SET_CATEGORY_ATTRIBUTE_OPTION',
        payload: {
            attributeId,
            activate: false
        }
    });
    dispatch(campaignCodeActions.validateCampaignCode());
};

export const throttledFetchCategoryAttributeSuggestions = (
    categoryId,
    shortDescription
) => async dispatch => {
    const action = () => {
        dispatch(
            fetchCategoryAttributeSuggestions(categoryId, shortDescription)
        );
    };
    suggestionsThrottle.execute(action);
};

export const fetchCategoryAttributeSuggestions = (
    categoryId,
    shortDescription
) => async (dispatch, getState) => {
    const featureSwitches = getState().environment.featureSwitches;
    if (!featureSwitches['syi-attribute-suggestions']) {
        return;
    }

    if (!categoryId || !shortDescription) {
        dispatch({
            type: 'RESET_ATTRIBUTES_SUGGESTIONS'
        });
        return;
    }

    const splitTestGroup = getState().environment.splitTestGroups[
        'attribute_suggestion_extractor'
    ];
    if (!splitTestEnabled(splitTestGroup)) {
        return;
    }
    const extractorType = getAttributeSuggestionExtractor(splitTestGroup);

    try {
        let params = {
            categoryId,
            shortDescription,
            extractorType
        };
        const response = await defaultClient.post(
            `${ENDPOINTS.SELLING_SUGGEST_ATTRIBUTES}`,
            params,
            {
                ...axiosConfigs.authenticated
            }
        );

        dispatch({
            type: 'SET_CATEGORY_ATTRIBUTES_SUGGESTIONS',
            payload: {
                suggestedAttributes: response.data.attributeValues.termAttributeValues.map(
                    transformServerAttributeSuggestion
                ),
                suggestionsExtractorType: response.data.extractorType
            }
        });
    } catch (error) {
        dispatch({
            type: 'RESET_ATTRIBUTES_SUGGESTIONS'
        });
        throw error;
    }
};

const splitTestEnabled = splitTest => splitTest !== 'control';

const getAttributeSuggestionExtractor = splitTest => {
    switch (splitTest) {
        case 'code':
            return 0;
        case 'tradera':
            return 1;
        case 'google':
            return 2;
        default:
            return 0;
    }
};

const transformServerAttributeSuggestion = attributeSuggestion => {
    return {
        id: attributeSuggestion.id,
        name: attributeSuggestion.name,
        values: attributeSuggestion.values
    };
};
